// Setup › DIGITAL EMPLOYEES › Deploys (`/c/{slug}/setup/deploys`; DC-04 routes, DC-05 screen):
// every deploy into this company, and the company side of the first-deploy approval (05 §DE
// configuration: "approver = target company#agency_operator | target company#owner (person
// only)"; managers read). A first deploy of a version waits here for the owner (or the company's
// agency operator); a pilot of a version with no verification skill shows **unverified** with its
// override's reason and end, and waits for the operator's override before anyone can approve it. Two
// calls on load: the deploys (with template name, version and override) and the DEs (names).
import { newIdempotencyKey } from "@companyos/sdk";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  EmptyState,
  Field,
  FieldLabel,
  Notice,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
} from "@companyos/ui";
import {
  queryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { CheckIcon, RocketIcon, XIcon } from "lucide-react";
import { useId, useState } from "react";
import type { Api } from "../../api/client";
import type { CompanyDeploy } from "../../api/types";
import type { CompanyAccess } from "../company";
import { formatDate, formatDateTime } from "../format";
import {
  awaitsApproval,
  awaitsOverride,
  DeployStateBadge,
  KIND_LABEL,
} from "../operator/deploys/deploy-parts";
import { problemLines } from "../operator/templates/template-parts";
import { useOperatorName } from "../operator-name";
import { digitalEmployeesQuery } from "../queries";
import { LoadError, Loading } from "../screen-parts";
import { DENSE_TABLE, RowAction } from "../settings/row-actions";
import { SetupPage } from "../settings/setup-page";
import { UnverifiedBadge } from "./unverified-badge";

export const companyDeployKeys = {
  all: (co: string) => ["company-deploys", co] as const,
};

/** The company's deploys, newest first (one page of 200: a company has few). */
export const companyDeploysQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: companyDeployKeys.all(co),
    queryFn: () => api.client.deploys.list({ params: { co }, query: { limit: 200 } }),
    // While something is moving (approved → handed off → confirmed), follow it.
    refetchInterval: (q) =>
      q.state.data?.data.some((d) => d.state === "approved" || d.state === "handed_off")
        ? 2000
        : false,
  });

/** 05: the owner (a person) or the company's agency operator approves; an admin reads only. */
export function mayApprove(company: CompanyAccess): boolean {
  return company.roles.includes("owner") || company.roles.includes("agency_operator");
}

export function CompanyDeploysScreen({ api, company }: { api: Api; company: CompanyAccess }) {
  const deploys = useQuery(companyDeploysQuery(api, company.id));
  const des = useInfiniteQuery(digitalEmployeesQuery(api, company.id));
  const names = new Map(
    (des.data?.pages.flatMap((p) => p.data) ?? []).map((d) => [d.membership_id, d.display_name]),
  );
  const rows = deploys.data?.data ?? [];
  const waiting = rows.filter((d) => awaitsApproval(d) && !awaitsOverride(d));
  return (
    <SetupPage title="Deploys" {...(deploys.data ? { count: rows.length } : {})}>
      {deploys.isPending ? (
        <Loading label="Loading deploys" rows={3} />
      ) : deploys.isError ? (
        <LoadError what="Deploys" error={deploys.error} />
      ) : !rows.length ? (
        <EmptyState variant="card" icon={RocketIcon} title="No deploys yet" />
      ) : (
        <div className="flex min-w-0 flex-col gap-3">
          {waiting.length ? (
            <Notice tone="warning" data-slot="company-awaits-approval">
              {waiting.length === 1
                ? "One deploy waits for approval."
                : `${waiting.length} deploys wait for approval.`}
            </Notice>
          ) : null}
          <Table data-slot="company-deploys" className={DENSE_TABLE}>
            <caption className="sr-only">Deploys into this company</caption>
            <TableHeader>
              <TableRow>
                <TableHead scope="col">Digital employee</TableHead>
                <TableHead scope="col">Configuration</TableHead>
                <TableHead scope="col" className="max-md:hidden">
                  Kind
                </TableHead>
                <TableHead scope="col">State</TableHead>
                <TableHead scope="col" className="max-lg:hidden">
                  Staged
                </TableHead>
                <TableHead scope="col">Decision</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((d) => (
                <DeployRow
                  key={d.id}
                  api={api}
                  company={company}
                  deploy={d}
                  de={names.get(d.de_membership_id) ?? `DE …${d.de_membership_id.slice(-6)}`}
                />
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </SetupPage>
  );
}

function DeployRow({
  api,
  company,
  deploy: d,
  de,
}: {
  api: Api;
  company: CompanyAccess;
  deploy: CompanyDeploy;
  de: string;
}) {
  const operator = useOperatorName();
  const pilot = d.verification_override;
  // DC-11: an empty self-serve agent's bundle is unverified too.
  const unverified = pilot !== null || awaitsOverride(d) || d.verification_state === "unverified";
  return (
    <TableRow data-deploy={d.id}>
      <TableHead scope="row" className="font-medium">
        {de}
      </TableHead>
      <TableCell>
        <span className="flex flex-col gap-1">
          <span>
            {d.template_name} v{d.version}
          </span>
          {pilot ? (
            <span
              className="block max-w-sm truncate text-xs text-muted-foreground"
              title={pilot.reason}
              data-slot="deploy-pilot"
            >
              Pilot until {formatDate(pilot.pilot_until)}: {pilot.reason}
            </span>
          ) : null}
        </span>
      </TableCell>
      <TableCell className="max-md:hidden">{KIND_LABEL[d.kind]}</TableCell>
      <TableCell>
        <span className="inline-flex flex-wrap gap-1.5">
          <DeployStateBadge deploy={d} />
          {unverified ? <UnverifiedBadge pilot={pilot} /> : null}
        </span>
        {d.error ? (
          <span className="mt-1 block text-xs text-danger-text">{d.error.code}</span>
        ) : null}
      </TableCell>
      <TableCell className="whitespace-nowrap text-muted-foreground max-lg:hidden">
        {formatDateTime(d.created_at)}
      </TableCell>
      <TableCell>
        {awaitsOverride(d) ? (
          <span className="text-sm text-muted-foreground">Waiting for {operator}'s override</span>
        ) : awaitsApproval(d) ? (
          mayApprove(company) ? (
            <Decide api={api} company={company} deploy={d} de={de} />
          ) : (
            <span className="text-sm text-muted-foreground">The owner approves</span>
          )
        ) : d.approved_at ? (
          <span className="text-sm text-muted-foreground">
            Approved {formatDate(d.approved_at)}
          </span>
        ) : (
          <span className="text-sm text-muted-foreground">—</span>
        )}
      </TableCell>
    </TableRow>
  );
}

/** Approve, or reject with a reason (the company side of the first-deploy approval). */
function Decide({
  api,
  company,
  deploy,
  de,
}: {
  api: Api;
  company: CompanyAccess;
  deploy: CompanyDeploy;
  de: string;
}) {
  const operator = useOperatorName();
  const qc = useQueryClient();
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");
  const reasonId = useId();
  const done = () => qc.invalidateQueries({ queryKey: companyDeployKeys.all(company.id) });
  const approve = useMutation({
    mutationFn: () =>
      api.client.deploys.approve({
        params: { co: company.id, id: deploy.id },
        idempotencyKey: newIdempotencyKey(),
      }),
    onSuccess: done,
  });
  const reject = useMutation({
    mutationFn: () =>
      api.client.deploys.reject({
        params: { co: company.id, id: deploy.id },
        idempotencyKey: newIdempotencyKey(),
        body: { reason: reason.trim() },
      }),
    onSuccess: async () => {
      await done();
      setRejecting(false);
    },
  });
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-1">
        <Button
          size="xs"
          disabled={approve.isPending}
          onClick={() => approve.mutate()}
          aria-label={`Approve the deploy to ${de}`}
        >
          <CheckIcon data-icon="inline-start" aria-hidden="true" />
          {approve.isPending ? "Approving…" : "Approve"}
        </Button>
        <RowAction
          icon={XIcon}
          label={`Reject the deploy to ${de}`}
          tip="Reject"
          className="hover:text-danger-text"
          onClick={() => setRejecting(true)}
        />
      </div>
      {approve.isError ? (
        <p role="alert" className="text-sm text-danger-text">
          {problemLines(approve.error, "The deploy could not be approved.").join(" ")}
        </p>
      ) : null}
      <AlertDialog
        open={rejecting}
        onOpenChange={(o) => {
          setRejecting(o);
          if (!o) reject.reset();
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reject the deploy to {de}?</AlertDialogTitle>
            <AlertDialogDescription>
              It is never handed off. {operator} sees your reason and can stage another version.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Field>
            <FieldLabel htmlFor={reasonId}>Reason</FieldLabel>
            <Textarea
              id={reasonId}
              rows={2}
              maxLength={2000}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </Field>
          {reject.isError ? (
            <Notice tone="danger" role="alert">
              {problemLines(reject.error, "The deploy could not be rejected.").join(" · ")}
            </Notice>
          ) : null}
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <Button
              variant="destructive"
              disabled={reject.isPending || !reason.trim()}
              onClick={() => reject.mutate()}
            >
              {reject.isPending ? "Rejecting…" : "Reject deploy"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
